import React, { useState, useMemo, useRef, forwardRef, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar } from 'react-chartjs-2';
import clsx from 'clsx';
import {
  MaterialReactTable,
  useMaterialReactTable,
} from 'material-react-table';
import { MRT_Localization_PL } from 'material-react-table/locales/pl';
import { Typography } from '@mui/material';

import { ChevronRight, PlusSign, Gears, MagnifyingGlass } from '../icons';
import Loading from '../common_components/Loading';
import { numberWithSpaces } from '../utils';

ChartJS.defaults.color = '#1f2937';
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
);

// Lightweight card component for selecting technology phase (mobile view)
function TechnologyPhaseCard({
  value,
  title,
  description,
  selected,
  onSelect,
  inputName = 'technology_phase',
}) {
  return (
    <label
      className={twMerge(
        'block rounded-lg border shadow-sm p-4 space-y-2 cursor-pointer transition-colors',
        'hover:border-primary focus-within:border-primary',
        selected ? 'ring-1 ring-success/40 bg-success/5' : 'border-base-300',
      )}
      style={selected ? { borderColor: 'hsl(var(--su))' } : undefined}
    >
      <div className="flex items-center gap-3">
        <input
          type="radio"
          name={inputName}
          value={value}
          checked={selected}
          onChange={onSelect}
          className="radio radio-success"
        />
        <span className="font-semibold text-lg select-none">{title}</span>
      </div>
      <p className="text-sm leading-snug m-0 opacity-80">{description}</p>
    </label>
  );
}

export default function App({
  csrfToken,
  urls,
  maxValueForTechnology,
  maxValueForAllTechnologies,
}) {
  const [resultsData, setResultsData] = useState();
  const [inputData, setInputData] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const resultsRef = useRef();
  const inputRef = useRef();

  return (
    <div className="prose w-full max-w-[--page-content-w] flex flex-col overflow-x-hidden">
      <>
        {/* TODO: unify this with APIS from normair and ATE, perhaps make as Context,
        and delete API models */}
        <WelcomeStep
          csrfToken={csrfToken}
          urls={urls}
          resultsData={resultsData}
          setResultsData={setResultsData}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
          setInputData={setInputData}
          inputData={inputData}
          resultsRef={resultsRef}
        />
        <InputStep
          csrfToken={csrfToken}
          urls={urls}
          resultsData={resultsData}
          setResultsData={setResultsData}
          setIsLoading={setIsLoading}
          isLoading={isLoading}
          setInputData={setInputData}
          inputData={inputData}
          resultsRef={resultsRef}
          maxValueForTechnology={maxValueForTechnology}
          maxValueForAllTechnologies={maxValueForAllTechnologies}
          ref={inputRef}
        />
        {(resultsData || isLoading) && (
          <ResultsAggregatedStep
            csrfToken={csrfToken}
            urls={urls}
            resultsData={resultsData}
            setResultsData={setResultsData}
            setIsLoading={setIsLoading}
            isLoading={isLoading}
            setInputData={setInputData}
            inputData={inputData}
            ref={resultsRef}
            inputRef={inputRef}
          />
        )}
      </>
    </div>
  );
}

// TODO: the fact that this is in 'Steps' is due to previous architecture -
//    divide into components in a better way
function WelcomeStep({ resultsRef }) {
  return (
    <>
      <h1 className="primary-header h1-as-h2 mt-0">
        ŚROD+EK - Pomiar środowiskowo-ekonomicznych efektów transformacji
        energetycznej <TutorialBtn />
      </h1>
      <TutorialBtn
        className={
          'inline-flex lg:hidden text-black shadow-primary static ml-6'
        }
      />
      <div>
        <div className="flex flex-col md:flex-row md:gap-8">
          <p className="flex-1 text-left sm:text-justify">
            Z punktu widzenia funkcjonowania polskiej gospodarki szczególnie
            istotne znaczenie ma <strong>określenie wpływu</strong> nowych
            instalacji energetycznych na wzrost produkcji krajowej i wartości
            dodanej, a także na powstawanie nowych miejsc pracy. W ten sposób
            możliwe staje się określenie skali pozytywnych{' '}
            <strong>efektów gospodarczych</strong>, które mogą zostać wywołane w
            polskiej gospodarce w efekcie prowadzenia transformacji
            energetycznej.
          </p>
          <p className="flex-1 text-left sm:text-justify mt-1 md:mt-[1.25rem]">
            Z drugiej strony nowe instalacje energetyczne powodują powstawanie
            śladu węglowego, którego pomiar jest istotny w kontekście
            wymiarowania
            <strong> efektów klimatycznych i środowiskowych</strong>{' '}
            towarzyszących transformacji energetycznej w Polsce.
          </p>
        </div>
        <div className="divider"></div>
        <p>
          Dla wybranego przez użytkownika{' '}
          <a
            href="#srodek:energetic-mix"
            className="text-primary font-bold italic"
          >
            miksu energetycznego
          </a>{' '}
          niniejsza aplikacja pozwala na{' '}
          <a
            className="text-primary font-bold italic"
            href="#srodek:submit-estimation"
          >
            estymację
          </a>{' '}
          wolumenu oraz sektorowego rozkładu zmiennych generowanych przez{' '}
          <a
            href="#srodek:technology-phase"
            className="text-primary font-bold italic"
          >
            instalację i użytkowanie
          </a>{' '}
          technologii tworzących zadany miks energetyczny. Zmienne brane pod
          uwagę:
        </p>
        <div className="flex flex-col md:flex-row justify-start sm:justify-center items-stretch md:items-center gap-3 md:gap-6 -mt-6 w-full px-2 overflow-x-hidden">
          <figure className="stats shadow shadow-success flex flex-col items-center w-full md:w-auto min-w-0 box-border p-5 sm:p-6 md:p-14 pt-4 md:pt-2 pb-5 md:pb-8 max-w-full text-center">
            <figcaption className="text-lg sm:text-2xl md:text-3xl mb-3 text-inherit uppercase font-light tracking-wide leading-snug whitespace-nowrap">
              <strong className="font-bold text-success">środ</strong>owiskowe:
            </figcaption>
            <ul className="prose border-none text-center text-sm sm:text-base leading-snug break-words flex flex-col items-center m-0 p-0">
              <li className="list-none">
                emisja CO<sub>2</sub>
              </li>
            </ul>
          </figure>
          <div className="flex w-full md:w-auto justify-center md:block my-0">
            <PlusSign className="size-12 md:size-10 flex-shrink-0" />
          </div>
          <figure className="stats shadow shadow-secondary flex flex-col items-center w-full md:w-auto min-w-0 box-border p-5 sm:p-6 md:p-14 pt-4 md:pt-2 pb-5 md:pb-8 max-w-full text-center">
            <figcaption className="text-lg sm:text-2xl md:text-3xl mb-3 text-inherit uppercase font-light tracking-wide leading-snug whitespace-nowrap">
              makro<strong className="font-bold text-secondary">ek</strong>
              onomiczne:
            </figcaption>
            <ul className="prose border-none text-center text-sm sm:text-base leading-snug break-words flex flex-col items-center gap-1 m-0 p-0">
              <li className="list-none">Produkcja krajowa</li>
              <li className="list-none">Zatrudnienie</li>
              <li className="list-none">Wartość dodana</li>
            </ul>
          </figure>
        </div>
        <p className="text-left sm:text-justify">
          Obliczenia prowadzone są w oparciu o tzw. metodologię input-output
          (IO). Szczegóły dot. matematycznej postaci wykorzystywanych modeli IO
          wraz z przykładowymi obliczeniami dla wybranej technologii (PV) można
          znaleźć w{' '}
          <a
            href="https://journals.agh.edu.pl/er/article/view/4819/2766"
            className="text-primary font-bold italic"
            target="_blank"
          >
            tym artykule
          </a>
          .
        </p>
      </div>
    </>
  );
}

const InputStep = forwardRef(function InputStep(
  {
    csrfToken,
    urls,
    setResultsData,
    setInputData,
    setIsLoading,
    inputData,
    resultsRef,
    maxValueForTechnology,
    maxValueForAllTechnologies,
  },
  ref,
) {
  const modalRef = useRef();
  const [modalText, setModalText] = useState('');
  const [selectedTechnologyPhase, setSelectedTechnologyPhase] = useState(
    inputData?.technologyPhase || 'installation',
  );

  useEffect(() => {
    if (inputData?.technologyPhase) {
      setSelectedTechnologyPhase(inputData.technologyPhase);
    }
  }, [inputData?.technologyPhase]);
  const handleSubmit = (event) => {
    event.preventDefault();

    const formData = new FormData(event.target);
    const data = Object.fromEntries(formData.entries());

    // validate values and display modal if validation fails
    const energeticalMixValues = Object.entries(data).filter(
      ([key, value]) => !key.startsWith('technology_phase'),
    );
    const energeticalMixNotPopulated = energeticalMixValues.every(
      ([key, value]) => {
        const numValue = parseFloat(value) || 0;
        return numValue <= 0;
      },
    );
    const technologyValueTooBig = energeticalMixValues.some(
      ([key, value]) => parseFloat(value) > maxValueForTechnology,
    );
    const technologyValuesSumTooBig =
      energeticalMixValues.reduce(
        (prev, [_, value]) => prev + parseFloat(value),
        0,
      ) > maxValueForAllTechnologies;

    if (energeticalMixNotPopulated) {
      setModalText(
        'Co najmniej jeden składnik miksu energetycznego musi być większy od 0',
      );
    } else if (technologyValueTooBig) {
      setModalText(
        `Maksymalna wartość dla pojedynczej technologii w miksie energetycznym to: ${numberWithSpaces(
          maxValueForTechnology,
        )}`,
      );
    } else if (technologyValuesSumTooBig) {
      setModalText(
        `Maksymalna suma wartości technologii w miksie energetycznym to: ${numberWithSpaces(
          maxValueForAllTechnologies,
        )}`,
      );
    }
    if (
      energeticalMixNotPopulated ||
      technologyValueTooBig ||
      technologyValuesSumTooBig
    ) {
      modalRef?.current.showModal();
      event.stopPropagation();
      return;
    }
    // ---

    const params = new URLSearchParams(data).toString();
    const url = `${event.target.action}?${params}`;

    setIsLoading(true);
    if (resultsRef.current) {
      resultsRef.current.scrollIntoView({ behavior: 'smooth' });
    }

    fetch(url, {
      method: 'GET',
      headers: {
        'X-CSRFToken': csrfToken,
        'Content-Type': 'application/json',
      },
    })
      .then((response) => response.json())
      .then((result) => {
        setResultsData(result);
        setIsLoading(false);
      });

    const { technology_phase: technologyPhase, ...energeticalMix } = data;
    setInputData({ technologyPhase, energeticalMix });
  };

  return (
    <>
      <form
        action={urls['srodek:technology_view']()}
        onSubmit={handleSubmit}
        ref={ref}
      >
        <StepMainHeader id={'srodek:technology-phase'}>
          Wybierz fazę życia technologii
        </StepMainHeader>
        <input
          type="hidden"
          name="technology_phase"
          value={selectedTechnologyPhase}
        />
        <div className="space-y-4 mt-4 md:hidden">
          <TechnologyPhaseCard
            value="installation"
            title="Instalacja"
            description="Faza życia technologii związania z produkcją lub zakupem dóbr i usług wymaganych do uruchomienia zadanego miksu energetycznego (np. zakup sterownika, budowa fundamentów pod panele fotowoltaiczne, wyprodukowanie kabli przesyłowych itp.)"
            selected={selectedTechnologyPhase === 'installation'}
            onSelect={() => setSelectedTechnologyPhase('installation')}
            inputName="technology_phase_mobile"
          />
          <TechnologyPhaseCard
            value="exploitation"
            title="Użytkowanie"
            description="Faza życia technologii związania produkcją lub zakupem dóbr i usług wymaganych w procesie korzystania z zadanego miksu energetycznego (np. przeglądy instalacji, serwisowanie i naprawa itp.)"
            selected={selectedTechnologyPhase === 'exploitation'}
            onSelect={() => setSelectedTechnologyPhase('exploitation')}
            inputName="technology_phase_mobile"
          />
        </div>
        <div className="hidden md:flex md:flex-col md:gap-4">
          <div className="flex items-center gap-4 mt-2">
            <div className="form-control btn btn-success cursor-pointer [&>*]:cursor-pointer w-[190px]">
              <label className="label gap-4">
                <input
                  type="radio"
                  name="technology_phase_desktop"
                  className="radio theme-controller border-white"
                  value="installation"
                  checked={selectedTechnologyPhase === 'installation'}
                  onChange={() => setSelectedTechnologyPhase('installation')}
                />
                <span className="text-lg">Instalacja</span>
              </label>
            </div>
            <p>
              Faza życia technologii związania z produkcją lub zakupem dóbr i
              usług wymaganych do uruchomienia zadanego miksu energetycznego
              (np. zakup sterownika, budowa fundamentów pod panele
              fotowoltaiczne, wyprodukowanie kabli przesyłowych itp.)
            </p>
          </div>
          <div className="flex items-center gap-4">
            <div className="form-control btn btn-success cursor-pointer [&>*]:cursor-pointer w-[190px]">
              <label className="label gap-4">
                <input
                  type="radio"
                  name="technology_phase_desktop"
                  className="radio theme-controller border-white"
                  value="exploitation"
                  checked={selectedTechnologyPhase === 'exploitation'}
                  onChange={() => setSelectedTechnologyPhase('exploitation')}
                />
                <span className="text-lg">Użytkowanie</span>
              </label>
            </div>
            <p>
              Faza życia technologii związania produkcją lub zakupem dóbr i
              usług wymaganych w procesie korzystania z zadanego miksu
              energetycznego (np. przeglądy instalacji, serwisowanie i naprawa
              itp.)
            </p>
          </div>
        </div>
        <StepMainHeader id="srodek:energetic-mix">
          Wybierz miks energetyczny
        </StepMainHeader>
        <div className="flex flex-wrap gap-4 justify-center md:justify-between">
          <MixInput
            text="Instalacje fotowoltaiczne prosumenckie (do 10kW)"
            name="pv1"
            defaultValue={inputData?.energeticalMix?.pv1}
          />
          <MixInput
            text="Instalacje fotowoltaiczne średniej wielkości (10-50kW)"
            name="pv2"
            defaultValue={inputData?.energeticalMix?.pv2}
          />
          <MixInput
            text="Duże instalacje fotowoltaiczne (50-950 kW)"
            name="pv3"
            defaultValue={inputData?.energeticalMix?.pv3}
          />
          <MixInput
            text="Wielkoskalowe instalacje fotowoltaiczne (ponad 950 kW)"
            name="pv4"
            defaultValue={inputData?.energeticalMix?.pv4}
          />
          <MixInput
            text="Kolektory słoneczne"
            name="pv5"
            defaultValue={inputData?.energeticalMix?.pv5}
          />
          <MixInput
            text="Pompy ciepła powietrzne"
            name="pv6"
            defaultValue={inputData?.energeticalMix?.pv6}
          />
          <MixInput
            text="Pompy ciepła gruntowe"
            name="pv7"
            defaultValue={inputData?.energeticalMix?.pv7}
          />
          <MixInput
            text="Małe elektrownie wodne"
            name="pv8"
            defaultValue={inputData?.energeticalMix?.pv8}
          />
          <MixInput
            text="Biogaz rolniczy"
            name="pv9"
            defaultValue={inputData?.energeticalMix?.pv9}
          />
          <MixInput
            text="Geotermia głęboka"
            name="pv10"
            defaultValue={inputData?.energeticalMix?.pv10}
          />
          <MixInput
            text="Elektrownie biomasowe CHP"
            name="pv11"
            defaultValue={inputData?.energeticalMix?.pv11}
          />
          <MixInput
            text="Elektrownie wiatrowe lądowe"
            name="pv12"
            defaultValue={inputData?.energeticalMix?.pv12}
          />
        </div>
        <div className="w-full flex justify-center mt-16 mb-10 px-4 sm:px-6">
          <button
            className={twMerge(
              'btn btn-lg btn-success flex gap-2 text-sm sm:text-base max-[332px]:text-xs w-full max-w-xs sm:max-w-sm md:btn-wide md:w-auto md:min-w-fit shrink-0',
            )}
            type="submit"
            id="srodek:submit-estimation"
          >
            <span className="text-center">Przeprowadź estymację</span>
            <Gears className="w-6 sm:w-7 md:w-8 flex-shrink-0" />
          </button>
        </div>
      </form>
      <dialog className="modal" ref={modalRef}>
        <div className="modal-box">
          <h3 className="font-bold text-lg text-warning">Błąd</h3>
          <p className="py-4">{modalText}</p>
          <div className="modal-action">
            <form method="dialog">
              <button className="btn btn-secondary">Zamknij</button>
            </form>
          </div>
        </div>
      </dialog>
    </>
  );
});

const ResultsAggregatedStep = forwardRef(function ResultsAggregatedStep(
  { resultsData, isLoading, inputRef, inputData },
  ref,
) {
  const detailsModal = useRef();
  const [activeTab, setActiveTab] = useState('costs'); // 'costs' or 'effects'
  const isInstallation = inputData?.technologyPhase === 'installation';

  const concatenateResult = (result) => {
    if (result) {
      if (result.multiplier !== 'None') {
        return `${result['value']} ${result['multiplier']}`;
      }
      return result['value'];
    }
  };

  useEffect(() => {
    if (ref?.current) {
      ref.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, []);

  return (
    <>
      <>
        <div ref={ref} className="mt-10 scroll-mt-24">
          <div className="lg:hidden">
            <StepMainHeader>Wyniki estymacji</StepMainHeader>

            <div
              role="tablist"
              className="tabs tabs-lg tabs-bordered justify-stretch mt-6 mb-8"
            >
              <button
                role="tab"
                className={clsx(
                  'tab no-underline flex-1',
                  activeTab === 'costs' && 'tab-active',
                )}
                onClick={() => setActiveTab('costs')}
              >
                Koszty
              </button>
              <button
                role="tab"
                className={clsx(
                  'tab no-underline flex-1',
                  activeTab === 'effects' && 'tab-active',
                )}
                onClick={() => setActiveTab('effects')}
              >
                Efekty
              </button>
            </div>

            <div className="space-y-4">
              {activeTab === 'costs' && (
                <>
                  <ResultCardNew
                    title="koszt całkowity"
                    unit={
                      resultsData?.summary?.costs?.['Koszt całkowity']?.unit
                    }
                    value={concatenateResult(
                      resultsData?.summary?.costs?.['Koszt całkowity'],
                    )}
                    description={
                      isInstallation
                        ? 'Całkowity koszt instalacji zadanego miksu energetycznego'
                        : 'Całkowity roczny koszt użytkowania zadanego miksu energetycznego'
                    }
                    isLoading={isLoading}
                  />
                  <ResultCardNew
                    title="popyt krajowy"
                    unit={resultsData?.summary?.costs?.['Popyt krajowy']?.unit}
                    value={concatenateResult(
                      resultsData?.summary?.costs?.['Popyt krajowy'],
                    )}
                    description={
                      isInstallation
                        ? 'Udział dóbr i usług krajowych w całkowitym koszcie instalacji zadanego miksu energetycznego'
                        : 'Udział dóbr i usług krajowych w całkowitym rocznym koszcie użytkowania zadanego miksu energetycznego'
                    }
                    isLoading={isLoading}
                  />
                  <ResultCardNew
                    title="local content"
                    unit={resultsData?.summary?.costs?.['Local content']?.unit}
                    value={concatenateResult(
                      resultsData?.summary?.costs?.['Local content'],
                    )}
                    description="Wskaźnik pokazujący udział dóbr i usług krajowych w całkowitym koszcie inwestycji"
                    isLoading={isLoading}
                  />
                </>
              )}

              {activeTab === 'effects' && (
                <>
                  <ResultCardNew
                    title="produkcja globalna"
                    unit={
                      resultsData?.summary?.economic_effects?.[
                        'Produkcja globalna'
                      ]?.unit
                    }
                    value={concatenateResult(
                      resultsData?.summary?.economic_effects?.[
                        'Produkcja globalna'
                      ],
                    )}
                    description={
                      isInstallation
                        ? 'Produkcja rozumiana jako ogół produktów i usług wytworzonych w polskiej gospodarce w rozpatrywanym przedziale czasowym wskutek przeprowadzenia instalacji zadanego miksu energetycznego'
                        : 'Produkcja rozumiana jako ogół produktów i usług wytworzonych w polskiej gospodarce w rozpatrywanym przedziale czasowym wskutek użytkowania przez okres roku zadanego miksu energetycznego'
                    }
                    isLoading={isLoading}
                  />
                  <ResultCardNew
                    title="wartość dodana"
                    unit={
                      resultsData?.summary?.economic_effects?.['Wartosc dodana']
                        ?.unit
                    }
                    value={concatenateResult(
                      resultsData?.summary?.economic_effects?.[
                        'Wartosc dodana'
                      ],
                    )}
                    description={
                      isInstallation
                        ? 'Wartość dodana rozumiana jako wielkość produkcji krajowej po odliczeniu kosztów pośrednich powstała w polskiej gospodarce wskutek przeprowadzenia instalacji zadanego miksu energetycznego'
                        : 'Wartość dodana rozumiana jako wielkość produkcji krajowej po odliczeniu kosztów pośrednich powstała w polskiej gospodarce wskutek użytkowania przez okres roku zadanego miksu energetycznego'
                    }
                    isLoading={isLoading}
                  />
                  <ResultCardNew
                    title="Zatrudnienie"
                    unit="liczba osób"
                    value={concatenateResult(
                      resultsData?.summary?.economic_effects?.['Zatrudnienie'],
                    )}
                    description={
                      isInstallation
                        ? 'Liczba pełnoetatowych kontraktów 1-rocznych powstałych wskutek przeprowadzenia instalacji zadanego miksu energetycznego'
                        : 'Liczba pełnoetatowych kontraktów 1-rocznych powstałych wskutek użytkowania przez okres roku zadanego miksu energetycznego'
                    }
                    isLoading={isLoading}
                  />
                  <ResultCardNew
                    title="emisja CO₂"
                    unit={
                      resultsData?.summary?.economic_effects?.['Emisja CO2']
                        ?.unit
                    }
                    value={concatenateResult(
                      resultsData?.summary?.economic_effects?.['Emisja CO2'],
                    )}
                    description={
                      isInstallation
                        ? 'Ślad węglowy powstały w łańcuchach produkcji w Polsce wygenerowanych wskutek przeprowadzenia instalacji zadanego miksu energetycznego'
                        : 'Ślad węglowy powstały w łańcuchach produkcji w Polsce wygenerowanych wskutek użytkowania przez okres roku zadanego miksu energetycznego'
                    }
                    isLoading={isLoading}
                  />
                </>
              )}
            </div>
          </div>
          <div className="hidden lg:flex w-full gap-14 items-stretch justify-stretch">
            <div className="flex flex-col basis-1/2 text-center gap-10 mb-4">
              <StepMainHeader>Estymowane koszty</StepMainHeader>
              <div className="grid grid-rows-[repeat(3,auto)] grid-cols-[auto_minmax(0,1fr)] gap-x-8 gap-y-10">
                <ResultCard
                  title={'koszt całkowity'}
                  unit={resultsData?.summary?.costs?.['Koszt całkowity']?.unit}
                  value={concatenateResult(
                    resultsData?.summary?.costs?.['Koszt całkowity'],
                  )}
                  description={
                    isInstallation
                      ? `Całkowity koszt instalacji zadanego miksu energetycznego`
                      : `Całkowity roczny koszt użytkowania zadanego miksu energetycznego`
                  }
                  isLoading={isLoading}
                />
                <ResultCard
                  title={`popyt krajowy`}
                  unit={resultsData?.summary?.costs?.['Popyt krajowy']?.unit}
                  value={concatenateResult(
                    resultsData?.summary?.costs?.['Popyt krajowy'],
                  )}
                  description={
                    isInstallation
                      ? `Udział dóbr i usług krajowych w całkowitym koszcie instalacji zadanego miksu energetycznego`
                      : `Udział dóbr i usług krajowych w całkowitym rocznym koszcie użytkowania zadanego miksu energetycznego`
                  }
                  isLoading={isLoading}
                />
                <ResultCard
                  title={`local content`}
                  unit={resultsData?.summary?.costs?.['Local content']?.unit}
                  value={concatenateResult(
                    resultsData?.summary?.costs?.['Local content'],
                  )}
                  description={`Wskaźnik pokazujący udział dóbr i usług krajowych w całkowitym koszcie inwestycji`}
                  isLoading={isLoading}
                />
              </div>
            </div>
            <div className="flex flex-col basis-1/2 text-center gap-10 mb-10">
              <StepMainHeader>
                Estymowane efekty gospodarcze i środowiskowe
              </StepMainHeader>
              <div className="grid grid-rows-[repeat(4,auto)] grid-cols-[auto_minmax(0,1fr)] gap-x-8 gap-y-10">
                <ResultCard
                  title={`produkcja globalna`}
                  unit={
                    resultsData?.summary?.economic_effects?.[
                      'Produkcja globalna'
                    ]?.unit
                  }
                  value={concatenateResult(
                    resultsData?.summary?.economic_effects?.[
                      'Produkcja globalna'
                    ],
                  )}
                  description={
                    isInstallation
                      ? `Produkcja rozumiana jako ogół produktów i usług wytworzonych w polskiej gospodarce w rozpatrywanym przedziale czasowym wskutek przeprowadzenia instalacji zadanego miksu energetycznego`
                      : `Produkcja rozumiana jako ogół produktów i usług wytworzonych w polskiej gospodarce w rozpatrywanym przedziale czasowym wskutek użytkowania przez okres roku zadanego miksu energetycznego`
                  }
                  isLoading={isLoading}
                />
                <ResultCard
                  title={`wartość dodana`}
                  unit={
                    resultsData?.summary?.economic_effects?.['Wartosc dodana']
                      ?.unit
                  }
                  value={concatenateResult(
                    resultsData?.summary?.economic_effects?.['Wartosc dodana'],
                  )}
                  description={
                    isInstallation
                      ? `Wartość dodana rozumiana jako wielkość produkcji krajowej po odliczeniu kosztów pośrednich powstała w polskiej gospodarce wskutek przeprowadzenia instalacji zadanego miksu energetycznego`
                      : `Wartość dodana rozumiana jako wielkość produkcji krajowej po odliczeniu kosztów pośrednich powstała w polskiej gospodarce wskutek użytkowania przez okres roku zadanego miksu energetycznego`
                  }
                  isLoading={isLoading}
                />
                <ResultCard
                  title={`Zatrudnienie`}
                  unit="liczba osób"
                  value={concatenateResult(
                    resultsData?.summary?.economic_effects?.['Zatrudnienie'],
                  )}
                  description={
                    isInstallation
                      ? `Liczba pełnoetatowych kontraktów 1-rocznych powstałych wskutek przeprowadzenia instalacji zadanego miksu energetycznego`
                      : `Liczba pełnoetatowych kontraktów 1-rocznych powstałych wskutek użytkowania przez okres roku zadanego miksu energetycznego`
                  }
                  isLoading={isLoading}
                />
                <ResultCard
                  title={`emisja CO<sub>2</sub>`}
                  unit={
                    resultsData?.summary?.economic_effects?.['Emisja CO2']?.unit
                  }
                  value={concatenateResult(
                    resultsData?.summary?.economic_effects?.['Emisja CO2'],
                  )}
                  description={
                    isInstallation
                      ? `Ślad węglowy powstały w łańcuchach produkcji w Polsce wygenerowanych wskutek przeprowadzenia instalacji zadanego miksu energetycznego`
                      : `Ślad węglowy powstały w łańcuchach produkcji w Polsce wygenerowanych wskutek użytkowania przez okres roku zadanego miksu energetycznego`
                  }
                  isLoading={isLoading}
                />
              </div>
            </div>
          </div>
        </div>
        <StepMainHeader className={'mb-4 mt-12'}>
          Przedstawienie graficzne
        </StepMainHeader>
        <p className="text-left mt-4">
          Sektory polskiej gospodarki, dla których przewidziany jest{' '}
          <strong>największy</strong> udział w globalnej produkcji, wartości
          dodanej, zatrudnieniu i śladzie węglowym generowanych przez zadany
          miks energetyczny:
        </p>
        {isLoading ? (
          <Loading />
        ) : (
          <div className="mt-4 grid grid-cols-1 gap-8 lg:grid-cols-2">
            <Plot
              color="#4a00ff" // daisy btn primary
              data={resultsData?.['data_to_plot']['Produkcja globalna']}
              title="Produkcja globalna"
            />
            <Plot
              color="#00a96e" // daisy btn success
              data={resultsData?.['data_to_plot']['Wartosc dodana']}
              title="Wartość dodana"
            />
            <Plot
              color="#000000"
              data={
                resultsData?.['data_to_plot']['Zatrudnienie']
                  ? {
                      ...resultsData['data_to_plot']['Zatrudnienie'],
                      unit: 'liczba osób',
                    }
                  : undefined
              }
              title="Zatrudnienie"
            />
            <Plot
              color="#ffbe00" // daisy btn warning
              data={resultsData?.['data_to_plot']['Emisja CO2']}
              title="Emisja CO₂"
            />
          </div>
        )}
      </>
      <div className="flex flex-col lg:flex-row gap-2 lg:gap-8 mt-6 px-4 sm:px-6 max-w-full overflow-x-hidden">
        <button
          className={twMerge(
            'btn btn-lg btn-secondary text-sm sm:text-base max-[332px]:text-xs mx-auto mt-4 sm:mt-6 flex gap-2 w-full max-w-sm lg:btn-wide lg:w-auto shrink-0',
          )}
          onClick={() => {
            if (inputRef.current) {
              inputRef.current.scrollIntoView({ behavior: 'smooth' });
            }
          }}
        >
          <ChevronRight className={clsx('w-3 -rotate-90 mt-1 flex-shrink-0')} />
          <span className="truncate">Edytuj dane</span>
        </button>
        <button
          className={clsx(
            'btn btn-lg btn-primary text-sm sm:text-base max-[332px]:text-xs mx-auto mb-6 sm:mb-10 mt-6 flex gap-2 w-full max-w-sm lg:btn-wide lg:w-auto shrink-0',
            isLoading && 'btn-disabled',
          )}
          onClick={() => {
            detailsModal?.current?.showModal();
          }}
        >
          <MagnifyingGlass className="size-6 sm:size-7 stroke-current flex-shrink-0" />
          <span className="text-center">Wyniki szczegółowe</span>
        </button>
      </div>
      <ResultsDetailsStep ref={detailsModal} resultsData={resultsData} />
    </>
  );
});

const ResultsDetailsStep = forwardRef(function ResultsDetailsStep(
  { resultsData },
  ref,
) {
  const data = resultsData?.economic_effects;

  const columns = useMemo(
    () => [
      {
        accessorKey: 'Sektor',
        header: 'Sektor',
        size: 400,
      },
      {
        accessorKey: 'Produkcja globalna',
        header: 'Produkcja globalna',
      },
      {
        accessorKey: 'Wartosc dodana',
        header: 'Wartosc dodana',
      },
      {
        accessorKey: 'Zatrudnienie',
        header: 'Zatrudnienie',
      },
      {
        accessorKey: 'Emisja CO2',
        header: 'Emisja CO2',
      },
    ],
    [],
  );

  const table = useMaterialReactTable({
    columns,
    data: data || [],
    enableFullScreenToggle: false,
    enablePagination: false,
    enableHiding: false,
    enableStickyHeader: true,
    enableColumnFilters: false,
    enableColumnActions: false,
    enableDensityToggle: false,
    localization: MRT_Localization_PL,
    muiTableContainerProps: {
      sx: { maxHeight: '400px' },
    },
    initialState: { showGlobalFilter: true }, // show search bar expanded by default
    renderBottomToolbarCustomActions: () => (
      <Typography
        variant="caption"
        sx={{
          p: 1,
          color: 'text.secondary',
          fontSize: '1rem',
          lineHeight: '1.25rem',
          paddingBlock: '17px',
        }}
      >
        Wszystkie sektory polskiej gospodarki wraz z ich przewidywanym udziałem
        w generowaniu globalnej produkcji, wartości dodanej, zatrudnienia i
        śladu węglowego przy zadanym miksie energetycznym.
      </Typography>
    ),
  });

  return (
    <>
      <dialog className="modal" ref={ref}>
        <div className="modal-box max-w-screen-xl p-0 max-h-[90vh] flex flex-col">
          <div className="p-4 pb-0 flex-shrink-0">
            <h2 className="mt-2">
              Szczegółowa estymacja dla wszystkich sektorów gospodarki
            </h2>
          </div>
          <div className="flex-1 overflow-y-auto px-4">
            <div className="w-full p-1">
              <div className="lg:hidden">
                <MobileAccordionView data={data} />
              </div>
              <div className="hidden lg:block">
                {data && data.length > 0 && (
                  <MaterialReactTable table={table} />
                )}
              </div>
            </div>
          </div>
          <div className="modal-action p-4 pt-0 flex-shrink-0">
            <form method="dialog">
              <button className="btn btn-secondary">Zamknij</button>
            </form>
          </div>
        </div>
      </dialog>
    </>
  );
});

function TutorialBtn({ className }) {
  return (
    <a
      href="https://www.youtube.com/watch?v=L2k7slrGdyQ&list=PLsr7358AKbHrVSgDYDyxCuae8mXe5Risx&index=1"
      target="_blank"
      className={twMerge(
        'btn btn-outline btn-sm gap-2 whitespace-nowrap flex-shrink-0 shadow shadow-white ml-10 text-white hover:bg-white hover:text-black hidden lg:inline-flex self-start',
        className,
      )}
      rel="noopener noreferrer"
      title="Zobacz samouczek video"
    >
      <span
        className="hidden"
        dangerouslySetInnerHTML={{
          __html:
            '<!--!Font Awesome Free 6.7.2 by @fontawesome - https://fontawesome.com License - https://fontawesome.com/license/free Copyright 2025 Fonticons, Inc.-->',
        }}
      ></span>
      <svg
        xmlns="http://www.w3.org/2000/svg"
        viewBox="0 0 576 512"
        className="h-4 w-4"
        fill="currentColor"
      >
        <path d="M0 128C0 92.7 28.7 64 64 64l256 0c35.3 0 64 28.7 64 64l0 256c0 35.3-28.7 64-64 64L64 448c-35.3 0-64-28.7-64-64L0 128zM559.1 99.8c10.4 5.6 16.9 16.4 16.9 28.2l0 256c0 11.8-6.5 22.6-16.9 28.2s-23 5-32.9-1.6l-96-64L416 337.1l0-17.1 0-128 0-17.1 14.2-9.5 96-64c9.8-6.5 22.4-7.2 32.9-1.6z" />
      </svg>
      Samouczek
    </a>
  );
}

function MixInput({ text, name, defaultValue }) {
  return (
    <label className="form-control w-full max-w-xs justify-end">
      <div className="label">
        <h3>
          <span>{text}</span>
        </h3>
      </div>
      <div className="flex items-center gap-4">
        <input
          type="number"
          defaultValue={defaultValue ?? '0'}
          min={0}
          className="input input-bordered text-lg w-full max-w-xs text-center"
          name={name}
        />
        <span className="text-lg font-bold">MW</span>
      </div>
    </label>
  );
}

function ResultCard({ title, unit, value, description, isLoading }) {
  return (
    <>
      <div className="stats shadow flex-grow flex-shrink-0 self-start">
        <div className="stat">
          <div className="stat-title lowercase">
            <span dangerouslySetInnerHTML={{ __html: title }}></span>{' '}
            {isLoading ? (
              <span className="inline-block skeleton h-4 w-8"></span>
            ) : (
              `[${unit}]`
            )}
          </div>
          <div className={clsx('stat-value mt-1', isLoading && 'skeleton h-8')}>
            {!isLoading && value}
          </div>
        </div>
      </div>
      <p className="flex-shrink flex-grow-0 text-left sm:text-justify m-0">
        {description}
      </p>
    </>
  );
}

function ResultCardNew({ title, unit, value, description, isLoading }) {
  return (
    <div className="card border border-base-300 bg-base-100 shadow-sm">
      <div className="card-body p-6">
        <div className="space-y-2">
          <div className="text-sm text-base-content/60 uppercase tracking-wide">
            <span dangerouslySetInnerHTML={{ __html: title }}></span>{' '}
            {isLoading ? (
              <span className="inline-block skeleton h-3 w-12"></span>
            ) : (
              `[${unit}]`
            )}
          </div>
          <div
            className={clsx(
              'text-3xl font-bold text-primary',
              isLoading && 'skeleton h-10 w-24',
            )}
          >
            {!isLoading && value}
          </div>
          <p className="text-sm text-base-content/80 leading-relaxed mt-3 mb-0">
            {description}
          </p>
        </div>
      </div>
    </div>
  );
}

function Plot({ data, title, color }) {
  const labels = Object.keys(data.data);
  const rawValues = labels.map((label) => Number(data.data[label] ?? 0));
  const maxValue = Math.max(...rawValues, 0);
  const formatter = new Intl.NumberFormat('pl-PL', {
    maximumFractionDigits: 0,
  });

  const entries = labels.map((label, index) => ({
    label,
    value: rawValues[index],
    percent: maxValue ? Math.min((rawValues[index] / maxValue) * 100, 100) : 0,
  }));
  const topEntries = entries.slice(0, 3);

  const dataFormatted = {
    labels,
    datasets: [
      {
        data: rawValues,
        backgroundColor: color,
      },
    ],
  };
  const chartRef = useRef(null);
  const mobileModalRef = useRef(null);

  useEffect(() => {
    if (!chartRef.current) {
      return;
    }
    const canvas = chartRef.current.canvas;
    if (canvas) {
      canvas.setAttribute(
        'aria-label',
        `Wykres słupkowy ${title} - dane dla różnych sektorów gospodarki`,
      );
      canvas.setAttribute('title', `Wykres słupkowy: ${title}`);
    }
  }, [title, dataFormatted]);

  const openModal = () => {
    mobileModalRef.current?.showModal();
  };

  const closeModal = () => {
    mobileModalRef.current?.close();
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: true,
        text: `${title} [${data.unit}]`,
        padding: {
          bottom: 20,
        },
        font: {
          family:
            'ui-sans-serif, system-ui, -apple-system, "Segoe UI", Roboto, Ubuntu, Cantarell, "Noto Sans", sans-serif, "Apple Color Emoji", "Segoe UI Emoji", "Segoe UI Symbol", "Noto Color Emoji"', // daisy UI default
          size: 24,
        },
      },
      tooltip: {
        bodyFont: {
          size: 14,
        },
        titleFont: {
          size: 16,
        },
        callbacks: {
          label: function (context) {
            return Math.round(context.parsed.x);
          },
        },
      },
    },
    scales: {
      x: {
        ticks: {
          font: {
            size: 15,
          },
        },
      },
      y: {
        ticks: {
          font: {
            size: 15,
          },
          callback: function (value, index) {
            let label = labels[index];

            const maxLineLength = 38; // characters per line
            if (label.length > maxLineLength) {
              label = label.slice(0, maxLineLength - 3) + '...';
            }
            return label;
          },
        },
      },
    },
  };

  return (
    <>
      <section className="space-y-4 lg:hidden">
        <div className="card bg-base-100 shadow-sm border border-base-300">
          <div className="card-body p-4 sm:p-5 space-y-4">
            <header className="space-y-1">
              <h3 className="text-lg font-semibold text-base-content">
                {title}
              </h3>
              <span className="text-sm font-medium text-base-content/70">
                Jednostka: {data.unit}
              </span>
            </header>
            <div className="space-y-3">
              {topEntries.map(({ label, value, percent }) => (
                <div key={label} className="space-y-1.5">
                  <div className="flex items-center justify-between gap-3">
                    <p className="text-sm font-medium text-base-content flex-1">
                      {label}
                    </p>
                    <span className="text-sm font-semibold text-base-content/80 whitespace-nowrap">
                      {`${formatter.format(value)} ${data.unit ?? ''}`.trim()}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-base-200 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${percent}%`, backgroundColor: color }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
            {entries.length > 3 && (
              <div className="space-y-2 pt-3">
                <button
                  className="btn btn-primary btn-sm sm:btn-md w-full"
                  onClick={openModal}
                >
                  Zobacz wszystkie sektory
                </button>
                <p className="text-xs text-base-content/60 italic mb-0">
                  Dotknij, aby wyświetlić pełną listę sektorów w trybie
                  pełnoekranowym.
                </p>
              </div>
            )}
          </div>
        </div>
      </section>
      <dialog
        ref={mobileModalRef}
        className="modal modal-bottom sm:modal-middle lg:hidden"
        role="dialog"
      >
        <div className="modal-box w-full max-w-2xl p-0 max-h-[90vh] flex flex-col bg-base-100">
          <div className="relative flex-1 overflow-y-auto p-5 sm:p-6 space-y-5">
            <header className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 pr-10">
              <div>
                <h3 className="text-lg font-semibold text-base-content">
                  {title}
                </h3>
                <button
                  type="button"
                  className="btn btn-ghost btn-sm absolute right-4 top-6 sm:top-8 rounded-full"
                  onClick={closeModal}
                  aria-label="Zamknij"
                >
                  ✕
                </button>
                <span className="text-sm font-medium text-base-content/70">
                  Jednostka: {data.unit}
                </span>
              </div>
            </header>
            <div className="space-y-3">
              {entries.map(({ label, value, percent }) => (
                <div key={label} className="space-y-1.5">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-sm font-medium text-base-content flex-1">
                      {label}
                    </p>
                    <span className="text-sm font-semibold text-base-content/80 whitespace-nowrap">
                      {`${formatter.format(value)} ${data.unit ?? ''}`.trim()}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-base-200 overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{ width: `${percent}%`, backgroundColor: color }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="modal-action modal-action--safe-bottom px-5 pt-4 mb-4 border-t border-base-200 bg-base-100">
            <form method="dialog" className="w-full" onSubmit={closeModal}>
              <div className="flex flex-col gap-3">
                <button className="btn btn-secondary w-full" type="submit">
                  Zamknij
                </button>
              </div>
            </form>
          </div>
        </div>
      </dialog>
      <div className="hidden lg:block h-80">
        <Bar ref={chartRef} options={options} data={dataFormatted} />
      </div>
    </>
  );
}

function MobileAccordionView({ data }) {
  const [expandedCard, setExpandedCard] = useState(null);
  const [filteredData, setFilteredData] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState('');
  const [sortOrder, setSortOrder] = useState('desc'); // desc dla malejąco, asc dla rosnąco

  const sortOptions = [
    { value: 'Sektor', label: 'Nazwa' },
    { value: 'Emisja CO2', label: 'Emisja CO2' },
    { value: 'Produkcja globalna', label: 'Produkcja' },
    { value: 'Wartosc dodana', label: 'Wartość' },
    { value: 'Zatrudnienie', label: 'Zatrudnienie' },
  ];

  useEffect(() => {
    let filtered = data || [];

    // Filtrowanie
    if (searchTerm) {
      filtered = filtered.filter((item) =>
        item.Sektor?.toLowerCase().includes(searchTerm.toLowerCase()),
      );
    }

    // Sortowanie
    if (sortBy) {
      filtered = [...filtered].sort((a, b) => {
        if (sortBy === 'Sektor') {
          // Sortowanie alfabetyczne dla nazw sektorów
          const aVal = String(a[sortBy] || '').toLowerCase();
          const bVal = String(b[sortBy] || '').toLowerCase();

          if (sortOrder === 'desc') {
            return bVal.localeCompare(aVal);
          } else {
            return aVal.localeCompare(bVal);
          }
        } else {
          // Sortowanie numeryczne dla pozostałych wartości
          const aVal =
            parseFloat(String(a[sortBy]).replace(/[^0-9.-]/g, '')) || 0;
          const bVal =
            parseFloat(String(b[sortBy]).replace(/[^0-9.-]/g, '')) || 0;

          if (sortOrder === 'desc') {
            return bVal - aVal;
          } else {
            return aVal - bVal;
          }
        }
      });
    }

    setFilteredData(filtered);
  }, [data, searchTerm, sortBy, sortOrder]);

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
  };

  const handleSort = (sortValue) => {
    if (sortValue === sortBy) {
      // Jeśli kliknięto to samo pole, zmień kolejność
      setSortOrder(sortOrder === 'desc' ? 'asc' : 'desc');
    } else {
      // Jeśli nowe pole, ustaw je z domyślną kolejnością
      setSortBy(sortValue);
      setSortOrder(sortValue === 'Sektor' ? 'asc' : 'desc'); // Nazwa alfabetycznie rosnąco, reszta malejąco
    }
  };

  const clearSort = () => {
    setSortBy('');
    setSortOrder('desc');
  };

  const toggleCard = (index) => {
    setExpandedCard(expandedCard === index ? null : index);
  };

  return (
    <div className="space-y-2 relative">
      {/* Search Input - Sticky */}
      <div className="sticky top-0 z-10 bg-base-100 pt-2 pb-3 mb-3 shadow-sm -mx-4 px-4">
        <div className="flex items-center gap-3 mb-3">
          <input
            type="text"
            placeholder="Szukaj sektora..."
            className="input input-bordered flex-1"
            onChange={(e) => handleSearch(e.target.value)}
          />
          <form method="dialog">
            <button className="btn btn-circle btn-ghost btn-sm">
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </form>
        </div>

        {/* Filter Pills - Horizontal Scroll */}
        <div
          className="flex gap-2 overflow-x-auto scrollbar-hide pb-1"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          {/* Default Filter Pill */}
          <button
            onClick={clearSort}
            className={`btn btn-sm h-8 min-h-8 px-3 whitespace-nowrap rounded-full border-0 ${
              !sortBy
                ? 'bg-primary text-primary-content hover:bg-primary/90'
                : 'bg-base-200 text-base-content/70 hover:bg-base-300'
            } transition-all duration-200`}
          >
            Domyślnie
          </button>

          {/* Sort Option Pills */}
          {sortOptions.map((option) => (
            <button
              key={option.value}
              onClick={() => handleSort(option.value)}
              className={`btn btn-sm h-8 min-h-8 px-3 whitespace-nowrap rounded-full border-0 flex items-center gap-1 ${
                sortBy === option.value
                  ? 'bg-primary text-primary-content hover:bg-primary/90'
                  : 'bg-base-200 text-base-content/70 hover:bg-base-300'
              } transition-all duration-200`}
            >
              {option.label}
              {sortBy === option.value && (
                <span className="text-xs ml-1 opacity-80">
                  {sortOrder === 'desc' ? '↓' : '↑'}
                </span>
              )}
            </button>
          ))}
        </div>
      </div>
      {/* Accordion Cards */}
      {filteredData.map((item, index) => (
        <div key={index} className="card border border-base-300 bg-base-100">
          <div
            className="card-body p-4 cursor-pointer"
            onClick={() => toggleCard(index)}
          >
            {/* Card Header */}
            <div className="flex justify-between items-center">
              <div>
                <h3 className="font-semibold text-base mb-1">{item.Sektor}</h3>
                <p className="text-sm text-base-content/60">
                  Emisja CO2: {item['Emisja CO2']}
                </p>
              </div>
              <div className="text-primary">
                <svg
                  className={`w-5 h-5 transition-transform ${
                    expandedCard === index ? 'rotate-180' : ''
                  }`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </div>
            </div>

            {/* Expanded Content */}
            {expandedCard === index && (
              <div className="mt-2 pt-2 border-t border-base-300 space-y-1">
                <div className="space-y-1">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-base-content/60">
                      Produkcja globalna:
                    </span>
                    <span className="font-semibold text-base-content text-right">
                      {item['Produkcja globalna']}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-base-content/60">
                      Wartość dodana:
                    </span>
                    <span className="font-semibold text-base-content text-right">
                      {item['Wartosc dodana']}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-base-content/60">
                      Zatrudnienie:
                    </span>
                    <span className="font-semibold text-base-content text-right">
                      {item['Zatrudnienie']}
                    </span>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
}

function StepMainHeader({ children, id, className }) {
  return (
    <h2 className={twMerge('primary-header mt-4 mb-0', className)} id={id}>
      {children}
    </h2>
  );
}
